import { Check, FileDown, Loader2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useToastStore } from "../../../../stores/toastStore";
import { MAX_PAGE_SIZE, safeFormatDate } from "../../../../utils/formatters";
import { DataTable, type Column } from "../../../components/DataTable";
import { Dropdown } from "../../../components/Dropdown";
import { SearchInput } from "../../../components/SearchInput";
import { Button } from "../../../components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "../../../components/ui/dialog";
import type { PagCycle } from "../data/pagTypes";
import {
  PAG_DRILLDOWN_STATUS,
  changePagGroupReviewStatus,
  changePagReviewStatus,
  fetchPagGroupDrilldown,
  fetchPagPreviousCycleDrilldown,
  fetchPagPreviousCycleGroupDrilldown,
  fetchPagPreviousCycleServiceRoleDrilldown,
  fetchPagPreviousCycleUserDrilldown,
  fetchPagRoleDrilldown,
  fetchPagServiceRoleDrilldown,
  fetchPagUserDrilldown,
  type PagDrilldownRequest,
  type PagDrilldownScope,
  type PagDrilldownStatus,
  type PagGroupDrilldownRow,
  type PagReviewAction,
  type PagReviewTargetType,
  type PagRoleDrilldownRow,
  type PagServiceRoleDrilldownRow,
  type PagUserDrilldownRow,
} from "../services/pagApi";

/* ── Scope options ──────────────────────────────────────────────────── */

const SCOPE_OPTIONS: { value: PagDrilldownScope; label: string }[] = [
  { value: "groups", label: "Groups" },
  { value: "roles", label: "Roles" },
  { value: "serviceRoles", label: "Service Roles" },
  { value: "users", label: "Users" },
];

type AnyRow =
  | PagUserDrilldownRow
  | PagGroupDrilldownRow
  | PagRoleDrilldownRow
  | PagServiceRoleDrilldownRow;

/** Every scope's endpoint takes the same request and answers with the same
 *  envelope, so one signature covers all of them. */
type PagDrilldownFetcher = (
  body: PagDrilldownRequest,
  page: number,
  size: number,
) => Promise<{ data: AnyRow[]; totalElements?: number; count?: number }>;

const DRILLDOWN_FETCHERS: Record<
  PagCycle,
  Partial<Record<PagDrilldownScope, PagDrilldownFetcher>>
> = {
  current: {
    groups: fetchPagGroupDrilldown,
    roles: fetchPagRoleDrilldown,
    serviceRoles: fetchPagServiceRoleDrilldown,
    users: fetchPagUserDrilldown,
  },
  previous: {
    groups: fetchPagPreviousCycleGroupDrilldown,
    roles: fetchPagPreviousCycleDrilldown,
    serviceRoles: fetchPagPreviousCycleServiceRoleDrilldown,
    users: fetchPagPreviousCycleUserDrilldown,
  },
};

const REVIEW_TARGET_BY_SCOPE: Record<
  Exclude<PagDrilldownScope, "groups">,
  PagReviewTargetType
> = {
  roles: "roles",
  serviceRoles: "serviceRoles",
  users: "users",
};

/** Service roles read as a list per service instance, so the current cycle
 *  asks the server for that order instead of taking the API's default. */
const SERVICE_ROLE_SORT = { sortByColumn: "instanceId", sortingOrder: "asc" };

/* ── Shared cell renderers ──────────────────────────────────────────── */

const TEXT = "text-[13px] font-normal leading-5 text-[#334155] dark:text-white/80";
const TEXT_STRONG = "text-[13px] font-semibold leading-5 text-[#1e293b] dark:text-white";
const CELL = "align-middle!";

const W = {
  select: 60,
  groupName: 160,
  groupId: 215,
  username: 175,
  userId: 215,
  createdOn: 140,
  lastLogged: 170,
  attachedPolicy: 210,
  inlinePolicy: 160,
  severity: 145,
  status: 120,
  roleName: 250,
  serviceName: 200,
  serviceId: 215,
  policyName: 225,
  policyArn: 330,
  mfaEnabled: 120,
  mfaSerial: 215,
  accessLogin: 210,
  policyType: 140,
  access: 125,
  passwordLastUsed: 175,
  count: 130,
} as const;

const px = (n: number) => `${n}px`;
const sumWidths = (cols: { width?: string }[]) =>
  `${cols.reduce((t, c) => t + (parseFloat(c.width ?? "0") || 0), 0)}px`;

/* ── Pinned columns ─────────────────────────────────────────────────────
   The selection box and the name column stay put while the rest scroll.
   Sticky cells need their own opaque background (table rows here are
   transparent) and a z-index above the scrolling cells. The header is
   already `sticky top-0 z-20`, so its pinned cells must outrank that to
   survive both axes at once.

   The seam is drawn with box-shadow rather than a border: the table sets
   `border-collapse: collapse`, under which borders on a sticky cell do not
   travel with it. */
/* These must repaint the EXACT surface they cover, or the frozen columns
   read as a separate panel bolted to the side of the table.

   The header is the subtle one. DataTable stacks two backgrounds there:
   `bg-[#eef2f7] dark:bg-[#1b1c2e]` on <thead>, then
   `bg-[#eef2f7] dark:bg-white/[0.05]` on the header <tr>. In light both
   layers are the same colour, so copying #eef2f7 matched. In dark the
   row's 5% white sits ON TOP of #1b1c2e, and a pinned cell painted a solid
   #1b1c2e came out darker than every header cell beside it. #262738 is
   that blend resolved, so the seam disappears.

   Body rows carry no background of their own — they show the dialog's
   `bg-background` through — so the pinned cells claim the same token. */
const PIN_HEAD = "sticky z-30 bg-[#eef2f7] dark:bg-[#262738]";
const PIN_CELL = "sticky z-10 bg-background";

/* The frozen edge is drawn as a hairline that matches DataTable's own
   column separator exactly, so a pinned column is indistinguishable from
   any other until it actually overlaps something.

   It used to add an 8px gradient shade hanging off the right edge
   (slate-900/15, black/60 in dark). That shade was permanent rather than
   scroll-dependent, and it was what made the first two columns look like
   a floating panel with the rest of the grid sliding under them.

   Kept as a pseudo-element rather than a border: the table sets
   `border-collapse: collapse`, under which a border on a sticky cell does
   not travel with it. The sticky cell is its own positioning context, so
   `absolute` anchors correctly. */
const PIN_EDGE =
  "after:pointer-events-none after:absolute after:inset-y-0 after:-right-px after:w-px " +
  "after:bg-[#e2e8f0] dark:after:bg-white/[0.06]";

/* Written out in full because Tailwind scans source statically: a computed
   `left-[${W.select}px]` would never be generated. Keep in step with
   W.select above — the name column starts where the checkbox ends. */
const PIN_SELECT_LEFT = "left-0";
const PIN_NAME_LEFT = "left-[60px]";

/** Backend sends "N/A" strings and 0 epochs for absent values; both mean
 *  "nothing here" and should read the same way. */
const isBlank = (v: unknown) =>
  v === null || v === undefined || v === "" || v === "N/A" || v === 0 || v === "0";

function Plain({ value }: { value: unknown }) {
  if (isBlank(value)) return <span className="text-[13px] text-[#94A3B8] dark:text-white/40">N/A</span>;
  return <span className={TEXT}>{String(value)}</span>;
}

/** Epoch seconds (users/roles) or a pre-formatted string (groups). */
function DateCell({ value }: { value: number | string | null | undefined }) {
  if (isBlank(value)) return <Plain value={null} />;
  if (typeof value === "number") {
    return <span className={`whitespace-nowrap tabular-nums ${TEXT}`}>{safeFormatDate(value * 1000)}</span>;
  }
  return <span className={`whitespace-nowrap tabular-nums ${TEXT}`}>{String(value)}</span>;
}

/* ── Pills ──────────────────────────────────────────────────────────────
   Same shape and the same tint-on-transparent tones as the Criticality
   column in the Details table behind this modal, so a severity reads the
   same way wherever it appears.

   Width flexes rather than fixing at the 88px that column uses:
   "Uncategorized" is three times the length of "High". */
/* min-w, not w: every pill is the same 88px so a column of them reads as
   one stack rather than a ragged edge -- but "Uncategorized" is longer
   than that on its own and grows instead of being clipped. 88 matches the
   Criticality column in the table behind, and the 145px severity column
   has room for either. */
const BADGE =
  "inline-flex min-w-[88px] items-center justify-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-bold leading-none";

const NEUTRAL_TONE = "bg-slate-500/20 text-slate-400";

/** Whatever case the API sent, rendered as Title case. Values arrive as
 *  "High" today and "HIGH" would shout, which the house rule forbids -- and
 *  a pill is exactly where an unexpected ALL CAPS value would be loudest. */
const titleCase = (v: string) =>
  v.charAt(0).toUpperCase() + v.slice(1).toLowerCase();

const SEVERITY_TONE: Record<string, string> = {
  HIGH: "bg-orange-500/20 text-orange-400",
  MEDIUM: "bg-yellow-500/20 text-yellow-400",
  LOW: "bg-blue-500/20 text-blue-400",
  UNCATEGORIZED: NEUTRAL_TONE,
};

function SeverityBadge({ value }: { value: string | null | undefined }) {
  if (isBlank(value)) return <Plain value={null} />;
  const raw = String(value).trim();
  return (
    <span className={`${BADGE} ${SEVERITY_TONE[raw.toUpperCase()] ?? NEUTRAL_TONE}`}>
      {titleCase(raw)}
    </span>
  );
}

const STATUS_TONE: Record<string, string> = {
  pending: "bg-amber-500/20 text-amber-400",
  continue: "bg-emerald-500/20 text-emerald-400",
  remove: "bg-red-500/20 text-red-400",
};

/** "Enabled & 25/04/2025", or "Disabled" when there is nothing to date.
 *  One column rather than two: the flag on its own never answered the
 *  question the reviewer is actually asking, which is when it was last
 *  used. */
function AccessCell({ on, at }: { on: boolean; at?: number | null }) {
  if (!on) return <span className={TEXT}>Disabled</span>;
  const when = at ? safeFormatDate(at * 1000) : null;
  return (
    <span className={`whitespace-nowrap ${TEXT}`}>
      {when ? `Enabled & ${when}` : "Enabled"}
    </span>
  );
}

function StatusBadge({ value }: { value: string }) {
  if (isBlank(value)) return <Plain value={null} />;
  const raw = String(value).trim();
  return (
    <span className={`${BADGE} ${STATUS_TONE[raw.toLowerCase()] ?? NEUTRAL_TONE}`}>
      {titleCase(raw)}
    </span>
  );
}

/* ── Export ─────────────────────────────────────────────────────────── */

const csvCell = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;

/** CSV of the rows on screen, using the live column set so the export
 *  always matches what the scope is actually showing. */
function exportRows(rows: AnyRow[], columns: Column<AnyRow>[], name: string) {
  const cols = columns.filter((c) => c.key !== "__select");
  const head = cols.map((c) => csvCell(c.header)).join(",");
  const body = rows.map((r) =>
    cols
      .map((c) => csvCell((r as unknown as Record<string, unknown>)[String(c.key)]))
      .join(","),
  );
  const url = URL.createObjectURL(
    new Blob([[head, ...body].join("\r\n")], { type: "text/csv;charset=utf-8;" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = `${name.replace(/[^\w-]+/g, "-").toLowerCase()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/* ── Selection checkbox ─────────────────────────────────────────────── */

/* The box is drawn here rather than left to the browser. A native checkbox
   styled only with `accent-indigo-600` renders through the UA's own dark
   colour-scheme, and the checked fill came out near-invisible against the
   dark table — the tick read as an empty box, so a selected row looked
   unselected. Painting the states explicitly makes both themes certain.

   The input stays in the DOM (sr-only) rather than being replaced by a
   div, so it keeps its focus behaviour, keyboard toggle and label
   semantics for free. */
function CheckCell({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    /* `shrink-0` and `leading-none` are load-bearing, not tidying.

       DataTable wraps every BODY cell in `flex min-w-0 overflow-hidden`,
       but renders header cells through a plain block wrapper. That made
       the box a flex item in the rows and not in the header — and in a
       44px column with cell padding there is under 16px of free width, so
       the row copies were squashed to a stubby rectangle and clipped
       while the header's stayed a clean square. Refusing to shrink is
       what makes the two identical. */
    <label className="inline-flex shrink-0 cursor-pointer items-center leading-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        onClick={(e) => e.stopPropagation()}
        aria-label={label}
        className="peer sr-only"
      />
      {/* 1.5px at #64748B, not 1px at #94A3B8. The lighter hairline all but
          vanished against the white row, so an empty box read as no
          control at all rather than as an unticked one. */}
      <span
        className={`flex size-4 shrink-0 items-center justify-center rounded-[3px] border-[1.5px] transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-indigo-500/40 ${checked
            ? "border-indigo-600 bg-indigo-600 dark:border-indigo-400 dark:bg-indigo-400"
            : "border-[#64748B] bg-white hover:border-indigo-500 dark:border-white/55 dark:bg-transparent dark:hover:border-indigo-400"
          }`}
      >
        {checked && (
          <Check
            className="size-3 text-white dark:text-[#131320]"
            strokeWidth={3.5}
            aria-hidden
          />
        )}
      </span>
    </label>
  );
}

/* ── Main ───────────────────────────────────────────────────────────── */

export function PagDrilldownModal({
  open,
  onOpenChange,
  accountId,
  accountName,
  appCode,
  status,
  cycle = "current",
  onChanged,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  accountId: string;
  accountName: string;
  appCode: string;
  status: PagDrilldownStatus;
  /** Fired after Continue or Remove lands. The caller's Pending and
   *  Completed counts are computed server-side from the rows this just
   *  moved, so they are stale until it refetches. */
  onChanged?: () => void;
  /** "previous" is closed history: no selection, no actions, export only. */
  cycle?: PagCycle;
}) {
  const isPrevious = cycle === "previous";

  /* Read-only: rows are shown, not acted on -- no checkboxes, no
     Remove/Continue, export in their place.

     Two ways in. A previous cycle is closed history. And a COMPLETED
     drill-down is precisely the list of rows already continued or
     removed, so offering to continue or remove them again offers to redo
     a decision that has been made.

     Kept separate from isPrevious rather than folded into it: that flag
     also picks the endpoint, the default scope and the request body, and
     none of those change because a count was Completed rather than
     Pending. This one governs the chrome only. */
  const readOnly = isPrevious || status === "completed";

  /* Which scopes actually have an endpoint in this cycle. Anything without
     one shows an explanatory panel rather than an empty table that reads as
     "no data". */
  const scopeHasEndpoint = (s: PagDrilldownScope) =>
    Boolean(DRILLDOWN_FETCHERS[cycle][s]);

  const scopeLabel = (s: PagDrilldownScope) =>
    SCOPE_OPTIONS.find((o) => o.value === s)?.label ?? s;
  const showSuccess = useToastStore((s) => s.showSuccess);
  const showError = useToastStore((s) => s.showError);
  /* Groups opens both cycles now that the closed one has its own list. */
  const [scope, setScope] = useState<PagDrilldownScope>("groups");
  const [keyword, setKeyword] = useState("");
  /** Debounced copy of `keyword` — the search hits the server, so it must
   *  not fire once per keystroke. */
  const [appliedKeyword, setAppliedKeyword] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(MAX_PAGE_SIZE);
  const [rows, setRows] = useState<AnyRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* Keyed by row key, holding the ROW — a selection survives paging, and by
     then the row is no longer in `rows`, but the action still needs its id
     (and, for groups, its groupType). */
  const [selected, setSelected] = useState<Map<string, AnyRow>>(new Map());
  const [acting, setActing] = useState<PagReviewAction | null>(null);

  /* Reset everything when the modal is opened for a different account or a
     different count, so one drill-down never shows another's state. */
  useEffect(() => {
    if (!open) return;
    setScope("groups");
    setKeyword("");
    setAppliedKeyword("");
    setPage(0);
    setSelected(new Map());
    setRows([]);
    setTotal(0);
    setError(null);
  }, [open, accountId, status]);

  const handleKeywordChange = (next: string) => {
    setKeyword(next);
    if (!next.trim()) {
      setAppliedKeyword("");
      setPage(0);

    }
  };

  const submitKeyword = (value: string) => {
    const next = value.trim();
    if (next === appliedKeyword) return;
    setAppliedKeyword(next);
    setPage(0);
  };

  useEffect(() => {







    /* debounce replaced with Enter search */
  }, []);

  /* `id` alone is NOT unique per row. It is the POLICY id, and one policy
     is attached to many identities — AdministratorAccess comes back as id
     14 on every role that holds it. Keying on it alone made those rows
     share one key, so ticking one ticked every other row carrying the
     same policy.

     The row's absolute position is what actually identifies it, so that
     leads. `id` is kept alongside purely to make the key readable when
     debugging a selection. */
  const rowKeyOf = useCallback(
    (r: AnyRow, i: number) =>
      `${scope}:${page * pageSize + i}:${(r as { id?: number }).id ?? "n"}`,
    [scope, page, pageSize],
  );

  /** Drives the explanatory panel and disables the search box. `load` reads
   *  the map directly, so it needs no narrowing of its own. */
  const hasEndpoint = scopeHasEndpoint(scope);

  const load = useCallback(() => {
    const call = DRILLDOWN_FETCHERS[cycle][scope];
    if (!open || !call) {
      if (!call) {
        setRows([]);
        setTotal(0);
        setError(null);
      }
      return;
    }

    /* The previous-cycle endpoint takes accountId only — it is not filtered
       by review status, so `status` is left off the body rather than sent
       empty. The current cycle still needs it: Pending and Completed are
       the same endpoint with different status arrays. */
    const body: PagDrilldownRequest = {
      accountId,
      appCode,
      keyword: appliedKeyword,
      sortByColumn: "",
      sortingOrder: "",
      /* The closed cycle takes no sort: it is a settled record, served in
         the order the review left it. */
      ...(scope === "serviceRoles" && !isPrevious ? SERVICE_ROLE_SORT : {}),
      ...(isPrevious ? {} : { status: PAG_DRILLDOWN_STATUS[status] }),
    };

    let cancelled = false;
    setLoading(true);
    setError(null);
    call(body, page, pageSize)
      .then((res) => {
        if (cancelled) return;
        setRows(res?.data ?? []);
        /* The previous-cycle USER endpoint reports its total as `count`;
           every other drill-down uses `totalElements`. Reading both means
           the pager works whichever envelope arrives, instead of showing
           "0 records" under a full page of rows. */
        setTotal(res?.totalElements ?? res?.count ?? 0);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setRows([]);
        setTotal(0);
        setError(e instanceof Error ? e.message : "Could not load these records");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, cycle, scope, accountId, appCode, status, appliedKeyword, page, pageSize]);

  useEffect(() => {
    const cleanup = load();
    return cleanup;
  }, [load]);

  /* ── Columns ──────────────────────────────────────────────────────── */

  const pageKeys = useMemo(() => rows.map(rowKeyOf), [rows, rowKeyOf]);
  const allOnPageSelected = pageKeys.length > 0 && pageKeys.every((k) => selected.has(k));

  const toggleAllOnPage = () =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (allOnPageSelected) pageKeys.forEach((k) => next.delete(k));
      else rows.forEach((r, i) => next.set(rowKeyOf(r, i), r));
      return next;
    });

  const toggleOne = (key: string, row: AnyRow) =>
    setSelected((prev) => {
      const next = new Map(prev);
      next.has(key) ? next.delete(key) : next.set(key, row);
      return next;
    });

  const selectColumn: Column<AnyRow> = {
    key: "__select",
    width: px(W.select),
    align: "center",
    headerAlign: "center",
    headerClassName: `${PIN_HEAD} ${PIN_SELECT_LEFT}`,
    cellClassName: `${CELL} ${PIN_CELL} ${PIN_SELECT_LEFT}`,
    header: (
      <CheckCell
        checked={allOnPageSelected}
        onChange={toggleAllOnPage}
        label="Select all rows on this page"
      />
    ),
    render: (r, i) => {
      const k = rowKeyOf(r, i);
      return (
        <CheckCell
          checked={selected.has(k)}
          onChange={() => toggleOne(k, r)}
          label="Select row"
        />
      );
    },
  };

  const col = (
    key: string,
    header: string,
    width: number,
    render: (r: AnyRow) => React.ReactNode,
    opts: Partial<Column<AnyRow>> = {},
  ): Column<AnyRow> => ({
    key,
    header,
    width: px(width),
    align: "left",
    headerAlign: "center",
    cellClassName: CELL,
    /* Single-line clamp: values here are identifiers with no sensible wrap
       point, and every column has a fixed width. DataTable falls back to
       the cell's own text for the tooltip when a column supplies none, so
       the full string stays reachable on hover. */
    ellipsis: 1,
    /* Header text is nowrap in DataTable, so a long label would be clipped
       rather than wrapped — the tooltip keeps it reachable. */
    headerTooltip: header,
    tooltip: (r) => {
      /* AnyRow is a union of concrete row types, so it needs the
         unknown hop before an index signature. */
      const val = (r as unknown as Record<string, unknown>)[key];
      if (val !== undefined && val !== null && val !== "" && val !== "N/A" && val !== 0 && val !== "0") {
        return String(val);
      }
      return undefined;
    },
    render,
    ...opts,
  });

  const nameLeft = readOnly ? PIN_SELECT_LEFT : PIN_NAME_LEFT;

  /** The pinned name column: sits beside the selection box and carries the
   *  seam shadow, since it is the last column that stays put. */
  const nameCol = (
    key: string,
    header: string,
    width: number,
    label: (r: AnyRow) => string,
  ): Column<AnyRow> =>
    col(key, header, width, (r) => (
      <span className="text-[13px] font-semibold text-indigo-600 dark:text-indigo-400">
        {label(r)}
      </span>
    ), {
      /* Sits where the checkbox ends — but a closed cycle has no checkbox,
         so it must pin flush left or it leaves a 44px hole. */
      headerClassName: `${PIN_HEAD} ${PIN_EDGE} ${nameLeft}`,
      cellClassName: `${CELL} ${PIN_CELL} ${PIN_EDGE} ${nameLeft}`,
      tooltip: (r) => label(r),
    });

  const columns: Column<AnyRow>[] = useMemo(() => {
    const g = (r: AnyRow) => r as PagGroupDrilldownRow;
    const u = (r: AnyRow) => r as PagUserDrilldownRow;
    const ro = (r: AnyRow) => r as PagRoleDrilldownRow;
    const sr = (r: AnyRow) => r as PagServiceRoleDrilldownRow;

    /* A closed cycle is a record, not a worklist: nothing here can be
       actioned, so the selection column would be a dead control.

       Only previous-cycle ROLES belongs here alongside Groups: that
       endpoint is named "...Role..." but returns the group row shape. The
       previous-cycle Users and Service Roles endpoints return their own
       shapes, so they fall through to the branches below or every field
       reads N/A. */
    if (scope === "groups") {
      return [
        ...(readOnly ? [] : [selectColumn]),
        nameCol("groupName", "Group Name", W.groupName, (r) => g(r).groupName),
        col("groupId", "Group ID", W.groupId, (r) => <Plain value={g(r).groupId} />),
        col("userName", "Username", W.username, (r) => <Plain value={g(r).userName} />),
        col("userId", "User ID", W.userId, (r) => <Plain value={g(r).userId} />),
        col("createdDate", "User Created On", W.createdOn, (r) => (
          <DateCell value={g(r).createdDate} />
        )),
        col("passwordLastUsed", "User Last Logged On", W.lastLogged, (r) => (
          <DateCell value={g(r).passwordLastUsed} />
        )),
        col("policyName", "Attached Policy", W.attachedPolicy, (r) => (
          <Plain value={g(r).policyName} />
        )),
        col("inlinePolicy", "Inline Policy", W.inlinePolicy, (r) => (
          <Plain value={g(r).inlinePolicy} />
        )),
        col("severity", "Policy Severity", W.severity, (r) => (
          <SeverityBadge value={g(r).severity} />
        )),
        col("status", "Status", W.status, (r) => <StatusBadge value={g(r).status} />),
      ];
    }

    /* Service roles carry no policy type and no ARN, so the scope reports
       the instance the role is attached to instead. The previous cycle's own
       field names are normalised in pagApi, so one branch serves both. */
    if (scope === "serviceRoles") {
      return [
        ...(readOnly ? [] : [selectColumn]),
        nameCol("serviceName", "Service Name", W.serviceName, (r) => sr(r).serviceName),
        col("instanceId", "Service ID", W.serviceId, (r) => <Plain value={sr(r).instanceId} />),
        col("roleName", "Role Name", W.roleName, (r) => <Plain value={sr(r).roleName} />, {
          tooltip: (r) => sr(r).roleName,
        }),
        /* Unsized for the same reason as the roles scope: this scope
           declares few enough columns to leave slack under table-fixed, and
           one flexing column absorbs it so the grid ends flush. */
        col("policyName", "Policy Name", W.policyName, (r) => <Plain value={sr(r).policyName} />, {
          width: undefined,
          tooltip: (r) => sr(r).policyName,
        }),
        col("policySeverity", "Policy Severity", W.severity, (r) => (
          <SeverityBadge value={sr(r).policySeverity} />
        )),
        col("status", "Status", W.status, (r) => <StatusBadge value={sr(r).status} />),
      ];
    }

    if (scope === "roles") {
      return [
        ...(readOnly ? [] : [selectColumn]),
        nameCol("roleName", "Role Name", W.roleName, (r) => ro(r).roleName),
        col("policyName", "Policy Name", W.policyName, (r) => <Plain value={ro(r).policyName} />, {
          width: undefined,
          tooltip: (r) => ro(r).policyName,
        }),
        /* The ONE column in this scope with no fixed width, on purpose.
           Roles declares far fewer columns than users or groups, so its
           widths add up to well under the modal — and `table-fixed` left
           that slack sitting at the right-hand end as an empty strip past
           Status. An unsized column absorbs the remainder instead, so the
           grid always ends flush at any modal width.

           Policy Name carries it now that ARN is gone: it holds the
           longest remaining values, and every other column here keeps its
           width, so exactly one column flexes and the leftover cannot
           split unevenly. */
        col("policyType", "Policy Type", W.policyType, (r) => <Plain value={ro(r).policyType} />),
        col("severity", "Policy Severity", W.severity, (r) => (
          <SeverityBadge value={ro(r).severity} />
        )),
        col("status", "Status", W.status, (r) => <StatusBadge value={ro(r).status} />),
      ];
    }

    // users — both cycles, since the row shape is identical. The checkbox
    // follows readOnly: only a list that can still be acted on gets one.
    return [
      ...(readOnly ? [] : [selectColumn]),
      nameCol("userName", "Username", W.username, (r) => u(r).userName),
      col("userId", "User ID", W.userId, (r) => <Plain value={u(r).userId} />),
      /* MFA is reported off the device list rather than a flag: the row has
         no mfaEnabled of its own, and "has a device registered" is the same
         fact. The first device carries the serial and the date. */
      col("mfaEnabled", "MFA Enabled", W.mfaEnabled, (r) => (
        <span className={TEXT}>{u(r).mfaDevices?.length ? "Yes" : "No"}</span>
      )),
      col("mfaSerial", "MFA Device Sl. No", W.mfaSerial, (r) => (
        <Plain value={u(r).mfaDevices?.[0]?.serialNumber} />
      ), {
        tooltip: (r) => u(r).mfaDevices?.[0]?.serialNumber || undefined,
      }),
      col("mfaEnabledOn", "MFA Enabled On", W.createdOn, (r) => (
        <DateCell value={u(r).mfaDevices?.[0]?.enabledDate} />
      ), {
        tooltip: (r) => u(r).mfaDevices?.[0]?.enabledDate ? safeFormatDate(u(r).mfaDevices[0].enabledDate * 1000) : undefined,
      }),
      col("consoleAccess", "Console Access & Last Login", W.accessLogin, (r) => (
        <AccessCell on={u(r).consoleAccess} at={u(r).passwordLastUsed} />
      ), {
        tooltip: (r) => !u(r).consoleAccess ? "Disabled" : u(r).passwordLastUsed ? `Enabled & ${safeFormatDate(u(r).passwordLastUsed! * 1000)}` : "Enabled",
      }),
      col("cliAccess", "CLI Access & Last Login", W.accessLogin, (r) => (
        <AccessCell on={u(r).cliAccess} at={u(r).accessKeys?.[0]?.lastUsed} />
      ), {
        tooltip: (r) => !u(r).cliAccess ? "Disabled" : u(r).accessKeys?.[0]?.lastUsed ? `Enabled & ${safeFormatDate(u(r).accessKeys![0].lastUsed! * 1000)}` : "Enabled",
      }),
      col("policyType", "Policy Type", W.policyType, (r) => <Plain value={u(r).policyType} />),
      /* Sized, NOT flexed like the roles scope. Roles declares few enough
         columns to leave slack for an unsized one to absorb; users declares
         eleven and already overflows the modal. An unsized column there made
         table-fixed shrink every sized column to fit instead, which squeezed
         Policy Severity under its own nowrap header until it collided with
         Status. The table scrolls sideways here, so the widths must hold. */
      col("policyName", "Policy Name", W.policyName, (r) => <Plain value={u(r).policyName} />, {
        tooltip: (r) => u(r).policyName,
      }),
      col("severity", "Policy Severity", W.severity, (r) => (
        <SeverityBadge value={u(r).severity} />
      )),
      col("status", "Status", W.status, (r) => <StatusBadge value={u(r).status} />),
    ];
    // selectColumn closes over selection state, so it must be re-created
    // whenever that changes or the header checkbox goes stale.
  }, [scope, selected, pageKeys, allOnPageSelected]);

  const selectedCount = selected.size;

  /**
   * Applies Continue or Remove to the current selection.
   *
   * Groups go to their own endpoint as an ARRAY: a single selection can mix
   * membership rows with policy rows, and each id has to travel under the
   * type naming the table it belongs to. That type is per-row (`groupType`),
   * so the ids are bucketed by it rather than sent under one blanket type.
   */
  const runAction = (status: PagReviewAction) => {
    const picked = [...selected.values()];
    if (!picked.length || acting) return;

    setActing(status);

    const request =
      scope === "groups"
        ? (() => {
          const byType = new Map<PagReviewTargetType, number[]>();
          picked.forEach((r) => {
            const type =
              ((r as PagGroupDrilldownRow).groupType as PagReviewTargetType) ||
              "groupPolicy";
            const ids = byType.get(type) ?? [];
            ids.push(r.id);
            byType.set(type, ids);
          });
          return changePagGroupReviewStatus(
            [...byType.entries()].map(([type, ids]) => ({ ids, type, status })),
          );
        })()
        : changePagReviewStatus({
          ids: picked.map((r) => r.id),
          type: REVIEW_TARGET_BY_SCOPE[scope],
          status,
        });

    request
      .then(() => {
        showSuccess(
          status === "Continue" ? "Access continued" : "Access removed",
          `${picked.length} ${picked.length === 1 ? "record" : "records"} updated.`,
        );
        setSelected(new Map());
        // Re-read: the rows just actioned leave the Pending list entirely.
        load();
        onChanged?.();
      })
      .catch((e: unknown) =>
        showError(
          `Could not ${status.toLowerCase()}`,
          e instanceof Error ? e.message : "The change was not saved.",
        ),
      )
      .finally(() => setActing(null));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Compact drill-down, not a full-page table. Height is capped at
          MODAL_MAX_H and never grows with the row count — extra rows go to
          the table's own scroll area or to the next page. Width is generous
          because the columns are wide, but extra columns scroll rather than
          widening the dialog. */}
      <DialogContent
        showCloseButton={false}
        className="flex max-h-[70vh] w-[calc(100vw-4rem)] max-w-[1440px] flex-col gap-0 overflow-hidden p-0 sm:max-w-[1440px]"
      >
        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-border px-5 py-2.5">
          <DialogTitle className={TEXT_STRONG}>{accountName}</DialogTitle>

          <Dropdown
            value={scope}
            onChange={(v) => {
              setScope(v as PagDrilldownScope);
              setPage(0);
        
              setSelected(new Map());
            }}
            options={SCOPE_OPTIONS}
            size="sm"
            variant="pill"
            searchable
            className="min-w-[150px]"
          />

          <div className="ml-auto flex flex-wrap items-center gap-2">
            <SearchInput
              value={keyword}
              onChange={handleKeywordChange}
              onSubmit={submitKeyword}
              maxLength={120}
              disabled={!hasEndpoint}
              size="sm"
              className="w-[190px]"
            />

            {readOnly ? (
              <Button variant="outline" size="sm"
                onClick={() => exportRows(rows, columns, `${accountName}-previous-cycle`)}
                disabled={!rows.length}
                className="justify-start gap-1.5 rounded-lg px-3.5 text-sm font-semibold text-foreground hover:bg-muted disabled:cursor-default disabled:opacity-40"
              >
                <FileDown className="size-3.5" />
                Generate Report
              </Button>
            ) : (
              <>
                <Button variant="ghost" size="sm"
                  onClick={() => runAction("Remove")}
                  disabled={!selectedCount || !!acting}
                  title={selectedCount ? `Remove ${selectedCount} selected` : "Select rows first"}
                  className="justify-start gap-1.5 rounded-lg bg-[#F87171] px-4 text-sm font-semibold text-white hover:bg-[#EF4444] disabled:cursor-default disabled:opacity-40"
                >
                  {acting === "Remove" && <Loader2 className="size-3.5 animate-spin" />}
                  Remove
                </Button>
                <Button variant="ghost" size="sm"
                  onClick={() => runAction("Continue")}
                  disabled={!selectedCount || !!acting}
                  title={selectedCount ? `Continue ${selectedCount} selected` : "Select rows first"}
                  className="justify-start gap-1.5 rounded-lg bg-[#5EEAD4] px-4 text-sm font-semibold text-[#0F766E] hover:bg-[#2DD4BF] disabled:cursor-default disabled:opacity-40"
                >
                  {acting === "Continue" && <Loader2 className="size-3.5 animate-spin" />}
                  Continue
                </Button>
              </>
            )}

            {/* Part of the header row rather than floating off the modal's
                corner, so it reads as a control and cannot clip the border. */}
            <Button variant="ghost" size="icon"
              onClick={() => onOpenChange(false)}
              aria-label="Close"
              title="Close"
              className="ml-1 flex size-8 shrink-0 justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50"
            >
              <X className="size-4" />
            </Button>
          </div>
        </div>

        {/* ── Body ─────────────────────────────────────────────────────
            No overflow here: DataTable owns the single scroll container, so
            header and body move together and there is only ever one
            horizontal scrollbar. min-h-0 lets it shrink inside the capped
            dialog instead of pushing it taller. */}
        <div className="flex min-h-0 flex-1 flex-col px-5 py-3">
          {!hasEndpoint ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-16 text-center">
              <p className="text-[13px] font-semibold text-foreground">
                {scopeLabel(scope)} are not available yet
              </p>
              {/* Only the previous cycle can land here now - the current
                  cycle serves all four scopes. */}
              <p className="mt-1 text-[13px] text-muted-foreground">
                The previous cycle exposes Roles, Service Roles and Users so far.
                Pick one of those.
              </p>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={rows}
              rowKey={rowKeyOf}
              loading={loading}
              error={error}
              onRetry={load}
              emptyText="No Data Available"
              page={page}
              pageSize={pageSize}
              totalItems={total}
              onPageChange={setPage}
              onPageSizeChange={(s) => {
                setPageSize(s);
                setPage(0);
          
              }}
              itemLabel="records"
              responsive
              compact
              /* Derived from the columns actually rendered, so each scope
                 gets exactly the width its own columns need. */
              minTableWidth={sumWidths(columns)}
              /* Default is 100 chars, which would clip full policy ARNs.
                 The table scrolls sideways, so they can be shown whole. */
              maxCellChars={512}
              /* Caps the scrollable header+body area so ~9 compact rows show
                 before it scrolls. Subtracts the dialog's own header, body
                 padding and pager from the 70vh shell, so the modal height
                 stays constant no matter how many rows come back. */
              maxHeight="calc(70vh - 150px)"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
